/**
 * Inventory: stock levels, adjustments and the movement ledger.
 * Conventions: see `common.ts`.
 */
const inventory: Record<string, string> = {
  title: 'Inventory',
  subtitle: 'Stock on hand for every SKU, and the ledger behind each number.',

  'search.label': 'Search stock',

  'empty.search_title': 'No SKUs match that search',
  'empty.search_description': 'Try a different product name or SKU.',
  'empty.title': 'Nothing to track yet',
  'empty.description': 'Stock appears here once you add products to your catalog.',

  'table.product': 'Product',
  'table.in_stock': 'In stock',
  'table.reserved': 'Reserved',
  'table.available': 'Available',
  'level.available': '{count} available',
  'level.adjust': 'Adjust',
  'level.hide': 'Hide',

  'adjust.reason': 'Reason',
  'adjust.quantity': 'Quantity',
  'adjust.note': 'Note',
  'adjust.failed': 'Could not adjust stock.',

  'movements.title': 'Recent movements',
  'movements.empty': 'No movements recorded yet.',
  'movements.entry_with_note': '{kind} — {note}',
  'movements.in_stock': '{count} in stock',

  'search.placeholder': 'Search by product, SKU or barcode',
  'select_row': 'Select {name}',
  'total_skus_one': '{count} SKU',
  'total_skus_other': '{count} SKUs',

  'table.sku': 'SKU',
  'table.threshold': 'Low at',
  'table.value': 'Stock value',

  'status.in_stock': 'In stock',
  'status.low': 'Low stock',
  'status.out': 'Out of stock',
  'status.untracked': 'Not tracked',

  'filters.stock_status': 'Stock status',
  'filters.quantity': 'Available quantity',
  'filters.units': 'units',

  'sort.name_asc': 'Product A–Z',
  'sort.name_desc': 'Product Z–A',
  'sort.sku_asc': 'SKU A–Z',
  'sort.sku_desc': 'SKU Z–A',
  'sort.stock_asc': 'Least stock first',
  'sort.stock_desc': 'Most stock first',
  'sort.value_desc': 'Highest stock value',
  'sort.urgency': 'Needs attention first',

  'kpi.low_hint': 'Reorder before they run out',
  'kpi.out_hint': 'Shoppers cannot buy these',
  'kpi.units': 'Units on hand',
  'kpi.units_hint_one': '{count} SKU is not tracked',
  'kpi.units_hint_other': '{count} SKUs are not tracked',
  'kpi.value': 'Stock value',
  'kpi.value_hint': 'Sellable stock at your selling price',

  'adjust.projected': 'Leaves {quantity} available',
  'adjust.applied': '{name} now has {quantity} available.',

  'bulk.action': 'Adjust stock',
  'bulk.action_hint': 'Apply one reason and quantity to every ticked SKU',
  'bulk.title': 'Adjust stock in bulk',
  'bulk.description_one': 'This applies the same change to {count} selected SKU.',
  'bulk.description_other': 'This applies the same change to {count} selected SKUs.',
  'bulk.apply_one': 'Apply to {count} SKU',
  'bulk.apply_other': 'Apply to {count} SKUs',
  'bulk.adds_each': 'Adds {quantity} to each',
  'bulk.removes_each': 'Removes {quantity} from each',
  'bulk.note_placeholder': 'Delivery #4821, Monday stocktake…',
  'bulk.note_hint': 'One note across the batch — it is what makes the ledger readable later.',
  'bulk.done_one': 'Stock updated for {count} SKU.',
  'bulk.done_other': 'Stock updated for {count} SKUs.',

  'export.csv': 'Stock levels (CSV)',
  'export.done': 'Your stock export is downloading.',

  // What the merchant picks when adjusting stock by hand.
  'reason.PURCHASE': 'Stock received',
  'reason.ADJUSTMENT': 'Manual correction',
  'reason.RETURN': 'Customer return',
  'reason.DAMAGE': 'Damaged or lost',
  'reason.CANCELLATION': 'Order cancelled',

  // How a ledger line names its movement - lower case, as it reads in a list.
  'kind.PURCHASE': 'purchase',
  'kind.SALE': 'sale',
  'kind.RETURN': 'return',
  'kind.ADJUSTMENT': 'adjustment',
  'kind.CANCELLATION': 'cancellation',
  'kind.DAMAGE': 'damage',
  'kind.RESERVATION': 'reservation',
  'kind.RELEASE': 'release',
};

export default inventory;
